import React from 'react';
import {Autocomplete, Avatar, Box, Card, CardContent, Checkbox, Chip, FormControlLabel, Grid, Menu, MenuItem, Stack, TextField, Typography,} from '@mui/material';
import {Business as BusinessIcon, CalendarToday as CalendarIcon, Person as PersonIcon,} from '@mui/icons-material';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import {TaskStatus} from '../../../../../services/openapi/models/TaskStatus';
import {TaskWithImages} from '../hooks/useTaskDetail';
import {IKunde} from '../../Hausverwaltung/interfaces';
import {BauvorhabenWithID} from '../../../../../services/openapi/models/BauvorhabenWithID';

interface TaskStatusDetailsProps {
    task: TaskWithImages | null;
    editMode: boolean;
    isTaskArchived: boolean;
    isAdmin: boolean;
    getCurrentValue: (field: string) => any;
    getStatusIcon: (status?: TaskStatus) => React.ReactElement;
    getStatusLabel: (status?: TaskStatus) => string;
    getStatusColor: (status?: TaskStatus) => 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    getAssignedUserName: () => string;
    getBauvorhabenName: () => string;
    formatDate: (dateString: string) => string;
    statusMenuAnchor: HTMLElement | null;
    setStatusMenuAnchor: (anchor: HTMLElement | null) => void;
    assignmentMenuAnchor: HTMLElement | null;
    setAssignmentMenuAnchor: (anchor: HTMLElement | null) => void;
    bauvorhabenMenuAnchor: HTMLElement | null;
    setBauvorhabenMenuAnchor: (anchor: HTMLElement | null) => void;
    handleStatusChange: (status: TaskStatus) => void;
    handleAssignmentChange: (userId: string) => void;
    handleBauvorhabenChange: (bauvorhabenId: number | undefined) => void;
    handleFieldUpdate: (field: string, value: any) => void;
    users: IKunde[];
    bau: BauvorhabenWithID[];
}

export const TaskStatusDetails: React.FC<TaskStatusDetailsProps> = ({
                                                                        task,
                                                                        editMode,
                                                                        isTaskArchived,
                                                                        isAdmin,
                                                                        getCurrentValue,
                                                                        getStatusIcon,
                                                                        getStatusLabel,
                                                                        getStatusColor,
                                                                        getAssignedUserName,
                                                                        getBauvorhabenName,
                                                                        formatDate,
                                                                        statusMenuAnchor,
                                                                        setStatusMenuAnchor,
                                                                        assignmentMenuAnchor,
                                                                        setAssignmentMenuAnchor,
                                                                        bauvorhabenMenuAnchor,
                                                                        setBauvorhabenMenuAnchor,
                                                                        handleStatusChange,
                                                                        handleAssignmentChange,
                                                                        handleBauvorhabenChange,
                                                                        handleFieldUpdate,
                                                                        users,
                                                                        bau,
                                                                    }) => {
    return (
        <Card variant='outlined' sx={{mb: 3}}>
            <CardContent>
                <Grid container spacing={3}>
                    {/* Left Column */}
                    <Grid item xs={12} sm={6}>
                        <Stack spacing={2}>
                            {/* Status */}
                            <Box>
                                <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                    Status
                                </Typography>
                                <Box position='relative'>
                                    <Chip
                                        icon={getStatusIcon(getCurrentValue('status'))}
                                        label={getStatusLabel(getCurrentValue('status'))}
                                        color={getStatusColor(getCurrentValue('status'))}
                                        size='medium'
                                        onClick={
                                            !isTaskArchived && editMode
                                                ? (e) => setStatusMenuAnchor(e.currentTarget)
                                                : undefined
                                        }
                                        sx={{cursor: isTaskArchived || !editMode ? 'default' : 'pointer'}}
                                    />
                                    <Menu
                                        anchorEl={statusMenuAnchor}
                                        open={Boolean(statusMenuAnchor)}
                                        onClose={() => setStatusMenuAnchor(null)}>
                                        {Object.values(TaskStatus).map((status) => (
                                            <MenuItem
                                                key={status}
                                                onClick={() => handleStatusChange(status)}
                                                selected={getCurrentValue('status') === status}>
                                                <Box display='flex' alignItems='center' gap={1}>
                                                    {getStatusIcon(status)}
                                                    {getStatusLabel(status)}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </Box>
                            </Box>

                            {/* Verantwortliche */}
                            <Box>
                                <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                    Verantwortliche
                                </Typography>
                                {isAdmin && !isTaskArchived && editMode ? (
                                    <Box position='relative'>
                                        <Box
                                            onClick={(e) => setAssignmentMenuAnchor(e.currentTarget)}
                                            display='flex'
                                            alignItems='center'
                                            gap={1}
                                            sx={{
                                                ml: -1,
                                                cursor: 'pointer',
                                                padding: 1,
                                                borderRadius: 1,
                                                '&:hover': {backgroundColor: 'action.hover'},
                                            }}>
                                            <Avatar sx={{bgcolor: 'primary.main', width: 32, height: 32}}>
                                                <PersonIcon sx={{fontSize: 20}}/>
                                            </Avatar>
                                            <Typography variant='body1'>{getAssignedUserName()}</Typography>
                                        </Box>
                                        <Menu
                                            anchorEl={assignmentMenuAnchor}
                                            open={Boolean(assignmentMenuAnchor)}
                                            onClose={() => setAssignmentMenuAnchor(null)}>
                                            {users.map((user) => (
                                                <MenuItem
                                                    key={user.user_id}
                                                    onClick={() => handleAssignmentChange(user.user_id)}
                                                    selected={getCurrentValue('assigned_to') === user.user_id}>
                                                    {user.fullname || user.email}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Box>
                                ) : (
                                    <Box display='flex' alignItems='center' gap={1}>
                                        <Avatar sx={{bgcolor: 'primary.main', width: 32, height: 32}}>
                                            <PersonIcon sx={{fontSize: 20}}/>
                                        </Avatar>
                                        <Typography variant='body1'>{getAssignedUserName()}</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box>
                                <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                    Erstellt am
                                </Typography>
                                <Typography variant='body1'>{task && formatDate(task.created_at)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                    Rapport erstellen
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(getCurrentValue('rapport_allow'))}
                                            onChange={(e) => handleFieldUpdate('rapport_allow', e.target.checked)}
                                            color="primary"
                                            disabled={isTaskArchived || !editMode}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                        </Stack>
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} sm={6}>
                        <Stack spacing={2}>
                            {/* Fälligkeitsdatum */}
                            <Box>
                                <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                    Fälligkeitsdatum
                                </Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
                                    <DatePicker
                                        value={
                                            getCurrentValue('due_date') ? dayjs(getCurrentValue('due_date')) : null
                                        }
                                        onChange={
                                            !isTaskArchived && editMode
                                                ? (newValue) => {
                                                    if (newValue && newValue.isValid()) {
                                                        const lokalString = newValue
                                                            .startOf('day')
                                                            .format('YYYY-MM-DDTHH:mm:ss');
                                                        handleFieldUpdate('due_date', lokalString);
                                                    }
                                                }
                                                : undefined
                                        }
                                        disabled={isTaskArchived || !editMode}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                InputProps: {
                                                    startAdornment: (
                                                        <CalendarIcon color='action' sx={{fontSize: 20, mr: 1}}/>
                                                    ),
                                                    readOnly: true,
                                                },
                                            },
                                            actionBar: {
                                                actions: ['accept'],
                                            },
                                        }}
                                        views={['year', 'month', 'day']}
                                        openTo='day'
                                    />
                                </LocalizationProvider>
                            </Box>

                            {/* Projekt */}
                            <Box>
                                <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                    Projekt *
                                </Typography>
                                {editMode && !isTaskArchived ? (
                                    <Autocomplete
                                        freeSolo
                                        size='small'
                                        options={bau}
                                        getOptionLabel={(option) =>
                                            typeof option === 'string' ? option : option.bvh_name
                                        }
                                        value={
                                            getCurrentValue('bauvorhaben_id')
                                                ? bau.find((bvh) => bvh.id === getCurrentValue('bauvorhaben_id')) || null
                                                : getCurrentValue('projekt_name') || null
                                        }
                                        onChange={(_, newValue) => {
                                            if (newValue === null) {
                                                handleBauvorhabenChange(undefined);
                                                handleFieldUpdate('projekt_name', undefined);
                                            } else if (typeof newValue === 'string') {
                                                handleBauvorhabenChange(undefined);
                                                handleFieldUpdate('projekt_name', newValue);
                                            } else {
                                                handleBauvorhabenChange(newValue.id);
                                                handleFieldUpdate('projekt_name', undefined);
                                            }
                                        }}
                                        onInputChange={(_, newInputValue, reason) => {
                                            if (reason === 'input') {
                                                const matchingBvh = bau.find(
                                                    bvh => bvh.bvh_name.toLowerCase() === newInputValue.toLowerCase()
                                                );
                                                if (matchingBvh) {
                                                    handleBauvorhabenChange(matchingBvh.id);
                                                    handleFieldUpdate('projekt_name', undefined);
                                                } else {
                                                    handleBauvorhabenChange(undefined);
                                                    handleFieldUpdate('projekt_name', newInputValue || undefined);
                                                }
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Projekt wählen oder eingeben..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <BusinessIcon color='action' sx={{fontSize: 20, mr: 1}}/>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                <Typography variant="body2">{option.bvh_name}</Typography>
                                            </li>
                                        )}
                                    />
                                ) : (
                                    <Box display='flex' alignItems='center' gap={1} sx={{ml: -1, p: 1}}>
                                        <BusinessIcon color='action' sx={{fontSize: 20}}/>
                                        <Typography variant='body1'>
                                            {getBauvorhabenName() || getCurrentValue('projekt_name') || 'Kein Projekt'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>


                            {/* Zuletzt aktualisiert */}
                            <Box>
                                <Typography variant='body2' color='text.secondary' sx={{mb: 1}}>
                                    Zuletzt aktualisiert
                                </Typography>
                                <Typography variant='body1'>{task && formatDate(task.updated_at)}</Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};
